import {
  MaskFillColor,
  MaterialName,
  WhiteLayerMaskMode,
  type PreviewBaseProps,
  type WhiteLayerMask,
} from '@athena-lib/LivePreview/types';

type DrawOptions = {
  fillStyle?: MaskFillColor;
  globalCompositeOperation: GlobalCompositeOperation;
  filterMode: WhiteLayerMaskMode;
  imgWidth: number;
  imgHeight: number;
};

type MaskImage = {
  img: HTMLImageElement;
  color: string;
};

type RgbaColor = {
  r: number;
  g: number;
  b: number;
  a: number;
};

type LabColor = {
  l: number;
  a: number;
  b: number;
};

async function generateGrayscaleMask(
  width: number,
  height: number,
  whiteLayerMasks: WhiteLayerMask[],
): Promise<HTMLCanvasElement> {
  const whitelayerCanvas = document.createElement('canvas');
  const whitelayerContext = whitelayerCanvas.getContext('2d');
  if (!whitelayerContext) {
    console.error('failed to get context');
    return whitelayerCanvas;
  }
  whitelayerCanvas.width = width;
  whitelayerCanvas.height = height;

  // Draw full effect masks
  const loadedFullEffectMasks = await Promise.all(
    whiteLayerMasks
      .filter((mask) => mask.maskImgUrl !== '' && mask.mode == WhiteLayerMaskMode.None)
      .map(async (mask) => {
        return {
          img: await loadImage(mask.maskImgUrl),
          color: mask.colorRange,
          mode: mask.mode,
        };
      }),
  );

  whitelayerContext.fillStyle = MaskFillColor.Effect;
  whitelayerContext.globalCompositeOperation = 'source-over';
  whitelayerContext.imageSmoothingEnabled = true;
  whitelayerContext.imageSmoothingQuality = 'high';
  whitelayerContext.fillRect(0, 0, whitelayerCanvas.width, whitelayerCanvas.height);
  loadedFullEffectMasks.forEach((mask) => {
    whitelayerContext.drawImage(mask.img, 0, 0, whitelayerCanvas.width, whitelayerCanvas.height);
  });

  // Load Mix masks
  const loadedMixMasks = await Promise.all(
    whiteLayerMasks
      .filter((mask) => mask.maskImgUrl !== '' && mask.mode == WhiteLayerMaskMode.Mix)
      .map(async (mask) => {
        return {
          img: await loadImage(mask.maskImgUrl),
          color: mask.colorRange,
          mode: mask.mode,
        };
      }),
  );

  loadedMixMasks.forEach((mask) => {
    const rgbaColor = rgbaColorFromString(mask.color);
    const grayscale = convertToGrayscaleValue(rgbaColor);
    const colorString = `rgba(${grayscale}, ${grayscale}, ${grayscale}, 255)`;
    const mixCanvas = setMaskColor(mask.img, colorString);
    whitelayerContext.drawImage(mixCanvas, 0, 0, whitelayerCanvas.width, whitelayerCanvas.height);
  });

  return whitelayerCanvas;
}

function rgbaColorFromString(color: string): RgbaColor {
  const rgba = color.split(',').map(Number);
  return {
    r: rgba[0],
    g: rgba[1],
    b: rgba[2],
    a: rgba[3] !== undefined ? rgba[3] : 255, // Default alpha to 255 if not provided
  };
}

function convertToGrayscaleValue(color: RgbaColor): number {
  const colorLAB: LabColor = convertRgbToLab(color.r, color.g, color.b);
  const x = 0.2;
  const colorValue = (colorLAB.l / 100) * (color.a / 255) * x * 255;
  return colorValue;
}

function convertRgbToLab(r: number, g: number, b: number): LabColor {
  // normalize
  r = r / 255;
  g = g / 255;
  b = b / 255;

  // gamma correction
  r = r > 0.04045 ? Math.pow((r + 0.055) / 1.055, 2.4) : r / 12.92;
  g = g > 0.04045 ? Math.pow((r + 0.055) / 1.055, 2.4) : r / 12.92;
  b = b > 0.04045 ? Math.pow((r + 0.055) / 1.055, 2.4) : r / 12.92;

  // convert to XYZ
  const x = r * 0.4124564 + g * 0.3575761 + b * 0.1804375;
  const y = r * 0.2126729 + g * 0.7151522 + b * 0.072175;
  const z = r * 0.0193339 + g * 0.119192 + b * 0.9503041;

  // use D65 white reference
  const xRef = 0.95047;
  const yRef = 1.0;
  const zRef = 1.08883;
  let [xr, yr, zr] = [x / xRef, y / yRef, z / zRef];

  const epsilon = 0.008856;
  const kappa = 903.3;
  xr = xr > epsilon ? Math.pow(xr, 1 / 3) : (kappa * xr + 16) / 116;
  yr = yr > epsilon ? Math.pow(yr, 1 / 3) : (kappa * yr + 16) / 116;
  zr = zr > epsilon ? Math.pow(zr, 1 / 3) : (kappa * zr + 16) / 116;

  return {
    l: Math.max(0, Math.min(100, 116 * yr - 16)),
    a: Math.max(-128, Math.min(127, 500 * (xr - yr))),
    b: Math.max(-128, Math.min(127, 200 * (yr - zr))),
  };
}

export default async function generateMasks(
  props: PreviewBaseProps,
  shouldGenerateGrayscaleMask: boolean = false,
) {
  const imgSrc = props.sticker.whiteLayerMasks[0].maskImgUrl;
  const img = await loadImage(imgSrc);

  let whiteLayerMaskCanvas = null;
  if (shouldGenerateGrayscaleMask && props.material.type === MaterialName.Clear) {
    whiteLayerMaskCanvas = await generateGrayscaleMask(
      img.naturalWidth,
      img.naturalHeight,
      props.sticker.whiteLayerMasks,
    );
  } else {
    whiteLayerMaskCanvas = await generateMask(
      {
        imgWidth: img.naturalWidth,
        imgHeight: img.naturalHeight,
        fillStyle:
          props.maskBackground === WhiteLayerMaskMode.None
            ? MaskFillColor.Color
            : MaskFillColor.Effect,
        globalCompositeOperation:
          props.maskBackground === WhiteLayerMaskMode.None ? 'destination-out' : 'source-over',
        filterMode: WhiteLayerMaskMode.None,
      },
      props.maskBackground,
      props.sticker.whiteLayerMasks,
    );
  }

  const opacityMaskCanvas = await generateMask(
    {
      imgWidth: img.naturalWidth,
      imgHeight: img.naturalHeight,
      fillStyle:
        props.maskBackground === WhiteLayerMaskMode.Full
          ? MaskFillColor.Effect
          : MaskFillColor.Color,
      globalCompositeOperation:
        props.maskBackground === WhiteLayerMaskMode.Full ? 'source-over' : 'destination-out',
      filterMode: WhiteLayerMaskMode.Full,
    },
    props.maskBackground,
    props.sticker.whiteLayerMasks,
  );

  const b64WhiteLayerMask = whiteLayerMaskCanvas.toDataURL('image/png');
  const b64OpacityMask = opacityMaskCanvas.toDataURL('image/png');

  return {
    b64WhiteLayerMask,
    b64OpacityMask,
  };
}

async function generateMask(
  drawOptions: DrawOptions,
  backgroundMode: WhiteLayerMaskMode,
  whiteLayerMasks: WhiteLayerMask[],
  setBackground = true,
): Promise<HTMLCanvasElement> {
  const canvas = document.createElement('canvas');
  const context = canvas.getContext('2d');
  if (!context) {
    console.error('failed to get context');
    return canvas;
  }
  canvas.width = drawOptions.imgWidth;
  canvas.height = drawOptions.imgHeight;

  const loadedImages = await loadImagesForMasks(
    whiteLayerMasks,
    backgroundMode,
    drawOptions.filterMode,
  );

  drawToContext(context, canvas, loadedImages, drawOptions);

  return setBackground ? setMaskBackground(canvas) : canvas;
}

function drawToContext(
  context: CanvasRenderingContext2D,
  canvas: HTMLCanvasElement,
  loadedImages: MaskImage[],
  drawOptions: DrawOptions,
) {
  if (drawOptions.fillStyle) {
    context.fillStyle = drawOptions.fillStyle;
    context.fillRect(0, 0, canvas.width, canvas.height);
  }

  context.globalCompositeOperation = drawOptions.globalCompositeOperation;

  context.imageSmoothingEnabled = true;
  context.imageSmoothingQuality = 'high';

  loadedImages.forEach((image) => {
    context.drawImage(image.img, 0, 0, canvas.width, canvas.height);
  });
}

async function loadImagesForMasks(
  whiteLayerMasks: WhiteLayerMask[],
  backgroundMode: WhiteLayerMaskMode,
  filterMode: WhiteLayerMaskMode,
) {
  return await Promise.all(
    whiteLayerMasks
      .filter(
        (mask) =>
          mask.maskImgUrl !== '' &&
          ((backgroundMode === filterMode && mask.mode !== filterMode) ||
            (backgroundMode !== filterMode && mask.mode === filterMode)),
      )
      .map(async (mask) => {
        return {
          img: await loadImage(mask.maskImgUrl),
          color: mask.colorRange,
        };
      }),
  );
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = (event) => {
      console.error('Failed to load image', event);
      reject(new Error('Failed to load image'));
    };
    img.src = src;
  });
}

function setMaskBackground(canvas: HTMLCanvasElement): HTMLCanvasElement {
  const bgCanvas = document.createElement('canvas');
  const bgContext = bgCanvas.getContext('2d');
  if (!bgContext) {
    console.error('failed to get context');
    return bgCanvas;
  }
  bgCanvas.width = canvas.width;
  bgCanvas.height = canvas.height;
  bgContext.fillStyle = MaskFillColor.Effect;
  bgContext.fillRect(0, 0, canvas.width, canvas.height);
  bgContext.drawImage(canvas, 0, 0);

  return bgCanvas;
}
export async function generateLivePreviewMask(
  props: PreviewBaseProps,
): Promise<Record<string, string>> {
  const whiteLayerMasks = props.sticker.whiteLayerMasks;
  const backgroundMode = props.maskBackground;

  const imgSrc = whiteLayerMasks[0].maskImgUrl;
  const img = await loadImage(imgSrc);

  const width = img.naturalWidth;
  const height = img.naturalHeight;
  const whiteLayerMaskCanvas = await generateMask(
    {
      imgWidth: img.naturalWidth,
      imgHeight: img.naturalHeight,
      fillStyle: backgroundMode === WhiteLayerMaskMode.None ? MaskFillColor.Color : undefined,
      globalCompositeOperation:
        backgroundMode === WhiteLayerMaskMode.None ? 'destination-out' : 'source-over',
      filterMode: WhiteLayerMaskMode.None,
    },
    backgroundMode,
    whiteLayerMasks,
    false,
  );

  const mixLayerCanvas = await generateMask(
    {
      imgWidth: img.naturalWidth,
      imgHeight: img.naturalHeight,
      fillStyle: backgroundMode === WhiteLayerMaskMode.Mix ? MaskFillColor.Mix : undefined,
      globalCompositeOperation:
        backgroundMode === WhiteLayerMaskMode.Mix ? 'destination-out' : 'source-over',
      filterMode: WhiteLayerMaskMode.Mix,
    },
    backgroundMode,
    whiteLayerMasks,
    false,
  );

  let combinedCanvas = document.createElement('canvas');
  combinedCanvas.width = width;
  combinedCanvas.height = height;
  const combinedContext = combinedCanvas.getContext('2d');
  if (!combinedContext) {
    console.error('failed to get context');
    return {
      combinedMask: combinedCanvas.toDataURL('image/png'),
      mixMask: mixLayerCanvas.toDataURL('image/png'),
      whitelayerMask: whiteLayerMaskCanvas.toDataURL('image/png'),
    };
  }
  combinedCanvas = setMaskBackground(whiteLayerMaskCanvas);

  return {
    combinedMask: combinedCanvas.toDataURL('image/png'),
    mixMask: setMaskColor(mixLayerCanvas, MaskFillColor.Color).toDataURL('image/png'),
  };
}
function setMaskColor(
  canvas: HTMLCanvasElement | HTMLImageElement,
  color: string,
): HTMLCanvasElement {
  const colorCanvas = document.createElement('canvas');
  const colorContext = colorCanvas.getContext('2d');
  if (!colorContext) {
    console.error('failed to get context');
    return colorCanvas;
  }
  colorCanvas.width = canvas.width;
  colorCanvas.height = canvas.height;
  colorContext.fillStyle = color;
  colorContext.fillRect(0, 0, canvas.width, canvas.height);
  colorContext.globalCompositeOperation = 'destination-in';
  colorContext.drawImage(canvas, 0, 0);

  return colorCanvas;
}
